
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import AdminBrandId from "@/mixins/adminBrandId";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ConfirmModalModes } from "@/data/enums";

// Types
import { IProvider } from "@/models/providers";
import {
  ITermsAndConditionsProvider,
  ITermsAndConditionsProviderMethods
} from "@/models/providers/termsAndConditions";
import { IState } from "@/store";
import {
  ITermsAndConditions,
  ITermsAndConditionsForm
} from "@/models/termsAndConditions";
import { ILanguage } from "@/models/constants";
import { ITranslation } from "@/models/translations";

@Component({
  computed: {
    ...mapState({
      termsAndConditionsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.TERMS_AND_CONDITIONS}/termsAndConditionsScope`
        ](this.termsId);
      },
      termsAndConditions(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.TERMS_AND_CONDITIONS,
            scope: this.termsAndConditionsScope
          }) || null
        );
      },

      termsAndConditionsTranslationsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.TERMS_AND_CONDITIONS}/termsAndConditionsTranslationsScope`
        ](this.termsId);
      },
      termsAndConditionsTranslations(state: IState, getters) {
        return (
          getters[`data/listArray`]({
            storeModule: DataModules.TERMS_AND_CONDITIONS,
            scope: this.termsAndConditionsTranslationsScope
          }) || null
        );
      }
    })
  }
})
export default class TermsAndConditionsProvider
  extends Mixins(RequestPromises, AdminBrandId)
  implements IProvider {
  // Map state

  public termsAndConditions!: ITermsAndConditions;
  public termsAndConditionsScope!: string;

  public termsAndConditionsTranslations!: ITranslation[];
  public termsAndConditionsTranslationsScope!: string;

  // Props

  @Prop({ default: "current" })
  public termsId!: ITermsAndConditions["id"];

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessing = false;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.TERMS_AND_CONDITIONS,
      scope: this.termsAndConditionsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.TERMS_AND_CONDITIONS,
      scope: this.termsAndConditionsTranslationsScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getTermsAndConditionsData(this.isReloading);
      await this.getTermsAndConditionsTranslations(this.isReloading);

      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getTermsAndConditionsData(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/get`,
      {
        id: this.termsId,
        scope: this.termsAndConditionsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public getTermsAndConditionsTranslations(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/listTranslations`,
      {
        id: this.currentTermsId,
        scope: this.termsAndConditionsTranslationsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public createTermsAndConditions(data: ITermsAndConditionsForm) {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/create`,
      {
        data
      }
    );
  }

  public openPreviewMarkdownModal(termsAndConditions: string) {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/openPreviewMarkdownModal`,
      {
        parent: this,
        props: {
          termsAndConditions
        }
      }
    );
  }

  public openVersionHistoryModal() {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/openVersionHistoryModal`,
      {
        parent: this
      }
    );
  }

  public openTranslationModal(languageId: ILanguage["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/openTranslationModal`,
      {
        parent: this,
        props: {
          translation:
            _.find(this.termsAndConditionsTranslations, [
              "language_id",
              languageId
            ]) || null,
          languageId,
          termsAndConditionId: _.get(this.termsAndConditions, "terms.id")
        },
        events: {
          success: this.reloadData
        }
      }
    );
  }

  public async deleteTranslation(language: ILanguage) {
    if (!this.canManage) return;

    try {
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            mode: ConfirmModalModes.BUTTON,
            type: "is-danger",
            title: this.$t("_action.delete_translation"),
            confirmButtonText: this.$t("_action.delete"),
            closeButtonText: this.$t("_action.keep_translation"),
            message: this.$t("_sentence.terms.confirm_delete", {
              language: language.language
            })
          },
          events: {
            confirmed: async () => {
              await this.removeTranslation(language.id);
              await this.getTermsAndConditionsTranslations(true);
              this.$store.dispatch("toast/show", {
                message: this.$t("_sentence.translations.successfully_removed")
              });
              confirm.close();
            }
          }
        }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public removeTranslation(languageId: ILanguage["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.TERMS_AND_CONDITIONS}/saveTranslation`,
      {
        id: _.get(this.termsAndConditions, "terms.id"),
        data: {
          translations: [
            {
              language_id: languageId,
              name: null,
              terms: null
            }
          ]
        }
      }
    );
  }

  // Computed

  public get translations() {
    return _.map(
      this.termsAndConditionsTranslations || [],
      translation => translation.language_id
    );
  }

  public get currentTermsId() {
    return _.get(this.termsAndConditions, "terms.id", "current");
  }

  public get canManage() {
    return this.$userCan("create_terms_and_conditions");
  }

  // Guarding

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public guardFunctionalities = ["get_current_terms_and_conditions"];

  // Provide

  @Provide("termsAndConditionsProvider")
  public providerData(): ITermsAndConditionsProvider {
    return {
      brandId: this.brandId,
      canManage: this.canManage,
      current: this.termsAndConditions,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      translations: this.termsAndConditionsTranslations
    };
  }

  @Provide("termsAndConditionsProviderMethods")
  public get providerMethods(): ITermsAndConditionsProviderMethods {
    return {
      createTermsAndConditions: this.createTermsAndConditions,
      deleteTranslation: this.deleteTranslation,
      openPreviewMarkdownModal: this.openPreviewMarkdownModal,
      openTranslationModal: this.openTranslationModal,
      openVersionHistoryModal: this.openVersionHistoryModal,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $termsAndConditionsData: this.providerData(),
        $termsAndConditionsMethods: this.providerMethods
      })
    );
  }
}
